import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { Input, Select, Button, Space, Form } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";

import { BLUE_DARK, GRAY_400 } from "../../constant/colors";
import {
  selectAssigneesAndUserGroups,
  selectPotentialSavings,
  selectUser,
  selectUserOrGroupById
} from "../../store/selector";
import { formatAssignee } from "../../utils/formaters";
import { getPopoverHeight } from "./Tasks";
import { DatePicker } from "@tailormed/common-client/component/DatePicker";
import { PrimaryButton } from "../customComponent/Button";

const { Option } = Select;

const TasksEdit = ({ isFetching, setIsEditMode, editTaskData, editTask, createTask, isPatientView }) => {
  const [form] = Form.useForm();
  const { patientId } = useParams();

  const { t } = useTranslation();
  const currentAssignee = Form.useWatch("assignee", form);
  const [selectedAssigneeType, setSelectedAssigneeType] = useState(null);
  const [isSubmitActive, setIsSubmitActive] = useState(false);
  const currentUser = useSelector(selectUser);
  const assignees = useSelector(selectAssigneesAndUserGroups);
  const getUserOrGroupById = useSelector(selectUserOrGroupById);
  const potentialSavings = useSelector(selectPotentialSavings);

  const missingAssignee =
    !!currentAssignee && assignees.every(({ id }) => id?.toString() !== currentAssignee.toString())
      ? getUserOrGroupById(currentAssignee)
      : null;

  useEffect(() => {
    if (!assignees?.length) return;

    if (editTaskData) {
      // Editing existing task. Fall back to the wider users store so a non-assignable existing
      // assignee is preserved on the form (and rendered by name via the hidden Option below)
      // instead of being silently dropped to undefined.
      const assignee =
        assignees.find((a) => a.id?.toString() === editTaskData.assignee?.toString()) ||
        getUserOrGroupById(editTaskData.assignee);

      form.setFieldsValue({
        text: editTaskData.text,
        dueDate: editTaskData.dueDate && moment(editTaskData.dueDate),
        assignee: assignee?.id,
        assigneeType: assignee?.assigneeType,
        linkedPotentialSaving: editTaskData.linkedPotentialSaving
      });

      setSelectedAssigneeType(assignee?.assigneeType);
    } else if (selectedAssigneeType === null) {
      // Creating new task - set default assigneeType when assignees load
      const defaultAssignee = assignees?.find((assignee) => assignee.id?.toString() === currentUser?.id?.toString());
      if (defaultAssignee) {
        setSelectedAssigneeType(defaultAssignee.assigneeType);
      }
    }
  }, [assignees, editTaskData, currentUser?.id]);

  const addTaskHandler = () => {
    const { text, dueDate, assignee, linkedPotentialSaving } = form.getFieldsValue(true);
    const taskData = {
      text,
      assignee,
      assigneeType: selectedAssigneeType || 1,
      dueDate,
      linkedPotentialSaving,
      patient: patientId
    };

    if (editTaskData) {
      taskData.id = editTaskData.id;
      taskData.updatedBy = currentUser.id;
      editTask(taskData);
    } else {
      taskData.createdBy = currentUser.id;
      createTask(taskData);
    }
  };

  const isFormValid = () => {
    const formValues = form.getFieldsValue();
    const formErrors = form.getFieldsError();

    if (!formValues.text?.trim()) {
      return false;
    }

    return formErrors.every((field) => {
      return field.errors.length == 0;
    });
  };

  return (
    <Form
      form={form}
      onFinish={addTaskHandler}
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "0px 16px 16px",
        width: "100%",
        minHeight: getPopoverHeight(isPatientView, true),
        maxHeight: isPatientView ? 340 : undefined,
        overflow: "auto"
      }}
      layout="vertical"
      initialValues={{
        assignee: assignees?.find((assignee) => assignee.id?.toString() === currentUser?.id?.toString())?.id,
        assigneeType: assignees?.find((assignee) => assignee.id?.toString() === currentUser?.id?.toString())
          ?.assigneeType
      }}
      onFieldsChange={() => {
        setIsSubmitActive(isFormValid());
      }}
    >
      <Button
        type="text"
        style={{ color: BLUE_DARK, padding: 0, width: "110px" }}
        icon={<ArrowLeftOutlined />}
        onClick={() => setIsEditMode(false)}
        id="back_to_tasks_button"
      >
        {t("patientsTasks.back_to_tasks")}
      </Button>
      <Form.Item
        name="text"
        rules={[{ required: true, message: t("patientsTasks.this_is_required") }]}
        style={{ paddingTop: 10, flex: "none" }}
        label={t("patientsTasks.task_description")}
      >
        <Input.TextArea
          id="task_text_input"
          placeholder={t("patientsTasks.task")}
          autoSize={{ minRows: 1, maxRows: 4 }}
        />
      </Form.Item>
      <Space>
        <Form.Item name="dueDate" label={t("patientsTasks.due_date")}>
          <DatePicker id="task_due_date_picker" />
        </Form.Item>
        <Form.Item name="assignee" label={t("patientsTasks.assignee")}>
          <Select
            id="task_assignee_select"
            placeholder={t("patientsTasks.assignee")}
            allowClear
            style={{ width: "200px" }}
            showSearch={true}
            filterOption={(input, option) => {
              if (option.value === currentUser?.id) {
                return (
                  !input ||
                  formatAssignee({ firstName: currentUser.firstName, lastName: currentUser.lastName, type: "user" })
                    .toLowerCase()
                    .includes(input.toLowerCase())
                );
              }
              return option.children?.toLowerCase?.().indexOf(input.toLowerCase()) >= 0;
            }}
            filterSort={(optionA, optionB) => {
              const aIsMe = optionA.value === currentUser?.id;
              const bIsMe = optionB.value === currentUser?.id;
              const aIsSelected = optionA.value == currentAssignee && !aIsMe;
              const bIsSelected = optionB.value == currentAssignee && !bIsMe;
              if (aIsSelected && !bIsSelected) return -1;
              if (bIsSelected && !aIsSelected) return 1;
              if (aIsMe && !bIsMe) return -1;
              if (bIsMe && !aIsMe) return 1;
              return (optionA.children || "").toLowerCase().localeCompare((optionB.children || "").toLowerCase());
            }}
            onChange={(value) => {
              const selectedAssignee = assignees?.find((assignee) => assignee.id?.toString() === value?.toString());

              setSelectedAssigneeType(selectedAssignee?.assigneeType);
            }}
            optionLabelProp="displayName"
          >
            {missingAssignee && (
              <Option
                key={`hidden-${missingAssignee.id}`}
                value={missingAssignee.id}
                displayName={formatAssignee(missingAssignee)}
                disabled
                style={{ display: "none" }}
              >
                {formatAssignee(missingAssignee)}
              </Option>
            )}
            {currentUser && assignees.some(({ id }) => id?.toString() === currentUser.id?.toString()) && (
              <Option
                key="me"
                value={currentUser.id}
                displayName={formatAssignee({
                  firstName: currentUser.firstName,
                  lastName: currentUser.lastName,
                  type: "user"
                })}
                id="tasks_assignee_me"
              >
                {formatAssignee({ firstName: currentUser.firstName, lastName: currentUser.lastName, type: "user" })}{" "}
                <span style={{ color: GRAY_400 }}>{t("bulk_actions.current_user_suffix")}</span>
              </Option>
            )}
            {assignees
              ?.filter(({ id }) => id !== currentUser?.id)
              .map((assignee) => (
                <Option key={assignee.id} value={assignee.id} displayName={formatAssignee(assignee)}>
                  {formatAssignee(assignee)}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Space>
      {(potentialSavings?.length && isPatientView && (
        <Form.Item name="linkedPotentialSaving" label={t("patientsTasks.associated_program")}>
          <Select
            id="task_potential_saving_select"
            dropdownMatchSelectWidth={false}
            placeholder={t("tableFilters.potential_savings")}
            style={{ width: "100%" }}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {potentialSavings.map((p, index) => (
              <Option key={index} value={p.id}>
                {p.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )) ||
        null}
      <PrimaryButton
        id={editTaskData ? "update_task_button" : "submit_add_task_button"}
        loading={isFetching}
        disabled={!isSubmitActive}
        type="primary"
        htmlType="submit"
        style={{ width: "140px" }}
      >
        {editTaskData ? t("patientsTasks.update") : t("patientsTasks.add")}
      </PrimaryButton>
    </Form>
  );
};

export { TasksEdit };
